import type { CallPathStep, FlowPieceKind } from '@arcnet/contracts';
import { Icon } from '../../components/ui/Icon.js';
import { formatDuration, formatTime } from '../../lib/format.js';
import { KIND_GROUP } from '../manage/flows/model.js';
import { KIND_ICON } from '../manage/flows/HistoryList.js';
import { useLiveRouting } from '../manage/flows/useLive.js';
import type { Live } from '../manage/flows/model.js';

/**
 * How a call went through the account's routing (docs/41 §8): the number it came in on, each step
 * it reached and what happened there -- the key pressed, the hours' answer, how long it rang or
 * waited -- and who answered. It answers "why did my call go there?" without anybody reading a
 * flow chart. The steps come from the switch (fsxml.ts pathMark), on the caller's own leg.
 */

export interface PathRow {
  /** What the step was, for its icon. */
  kind: FlowPieceKind | 'voicemail' | 'outside';
  at: number;
  title: string;
  /** The step's kind in words, beside its name. */
  what: string;
  /** What happened there, in a few words. */
  said: string[];
  /** Seconds until the next step, or until the call ended. */
  seconds: number | null;
}

const fmtNumber = (digits: string) => {
  const m = /^27(\d{2})(\d{3})(\d{4})$/.exec(digits);
  return m ? `+27 ${m[1]} ${m[2]} ${m[3]}` : `+${digits}`;
};

/** The path as rows, named from the account's routing as it is now. Pure. */
export function pathRows(steps: CallPathStep[], live: Live, ended: number,
  answeredBy: { number: string | null; name: string | null; at: string } | null): PathRow[] {
  const named = (list: Array<{ id: string; name: string }>, id: string | undefined, what: string) =>
    list.find((x) => x.id === id)?.name ?? `A ${what} since deleted`;
  const rows: PathRow[] = [];
  const last = () => rows[rows.length - 1];
  for (const st of steps) {
    const r = st.r ?? '';
    switch (st.s) {
      case 'in': {
        const route = live.routes.find((x) => x.numbers.some((n) => n.e164.replace(/\D/g, '') === r));
        rows.push({ kind: 'inbound_route', at: st.t, title: route?.name ?? fmtNumber(r), what: 'Call came in', said: [`on ${fmtNumber(r)}`], seconds: null });
        break;
      }
      case 'open': last()?.said.push('within hours'); break;
      case 'closed': last()?.said.push('outside hours'); break;
      case 'ivr': rows.push({ kind: 'ivr', at: st.t, title: named(live.ivrs, r, 'menu'), what: 'IVR menu', said: [], seconds: null }); break;
      case 'key': {
        const menu = live.ivrs.find((m) => m.name === last()?.title);
        const label = menu?.options.find((o) => o.digit === r)?.label;
        last()?.said.push(`pressed ${r}${label ? ` (${label})` : ''}`);
        break;
      }
      case 'dial': last()?.said.push(`keyed extension ${r}`); break;
      case 'nokey': last()?.said.push('pressed nothing'); break;
      case 'badkey': last()?.said.push('pressed a wrong key'); break;
      case 'rg': rows.push({ kind: 'ring_group', at: st.t, title: named(live.groups, r, 'hunt list'), what: 'Hunt list', said: [], seconds: null }); break;
      case 'rgs': last()?.said.push(`then step ${r}`); break;
      case 'q': rows.push({ kind: 'queue', at: st.t, title: named(live.queues, r, 'queue'), what: 'Queue', said: [], seconds: null }); break;
      case 'qexit': last()?.said.push('left by the exit key'); break;
      case 'msg': rows.push({ kind: 'message', at: st.t, title: named(live.messages, r, 'message'), what: 'Message', said: ['played its recording'], seconds: null }); break;
      case 'hrs': rows.push({ kind: 'hours', at: st.t, title: named(live.hours, r, 'hours check'), what: 'Hours check', said: [], seconds: null }); break;
      // docs/41 §16. What a look up answered is never shown: only which way the call went.
      case 'clr': rows.push({ kind: 'caller', at: st.t, title: named(live.callers, r, 'caller check'), what: 'Caller check', said: [], seconds: null }); break;
      case 'match': last()?.said.push('on the list'); break;
      case 'nomatch': last()?.said.push(last()?.kind === 'lookup' ? 'another answer' : 'not on the list'); break;
      case 'lkp': rows.push({ kind: 'lookup', at: st.t, title: named(live.lookups, r, 'look up'), what: 'Look up', said: [], seconds: null }); break;
      case 'case': {
        const l = live.lookups.find((x) => x.name === last()?.title);
        const v = l?.cases[Number(r)]?.value;
        last()?.said.push(v ? `answered “${v}”` : 'answered');
        break;
      }
      case 'error': last()?.said.push('no answer in time'); break;
      case 'flw': rows.push({ kind: 'inbound_route', at: st.t, title: named(live.routes, r, 'call flow'), what: 'Went on to the call flow', said: [], seconds: null }); break;
      case 'loop': last()?.said.push('flows led round in a circle, so the call was ended'); break;
      case 'vm': {
        const box = live.boxes.find((b) => b.extension_number === r);
        rows.push({ kind: 'voicemail', at: st.t, title: box ? `${box.extension_number} ${box.extension_name}` : `Mailbox ${r}`, what: 'Voicemail', said: [], seconds: null });
        break;
      }
      case 'fwd': rows.push({ kind: 'outside', at: st.t, title: fmtNumber(r), what: 'Forwarded to an outside number', said: [], seconds: null }); break;
      default: break;
    }
  }
  rows.forEach((row, i) => {
    const next = rows[i + 1];
    row.seconds = Math.max(0, (next ? next.at : ended) - row.at);
    // How long a list rang or a queue held, and how it ended, read from what came next.
    if (row.kind === 'ring_group' || row.kind === 'queue') {
      const verb = row.kind === 'ring_group' ? 'rang' : 'waited';
      if (next) row.said.push(`${verb} ${formatDuration(row.seconds)}, nobody answered`);
      else if (answeredBy) {
        const who = [answeredBy.number, answeredBy.name].filter(Boolean).join(' ');
        const after = Math.max(0, Math.round(new Date(answeredBy.at).getTime() / 1000 - row.at));
        row.said.push(`answered by ${who} after ${formatDuration(after)}`);
        // The time at the step is until somebody answered; the conversation is not the queue's.
        row.seconds = after;
      } else row.said.push(`${verb} ${formatDuration(row.seconds)}, then the caller hung up`);
    }
    if (row.kind === 'voicemail' && !next) row.said.push('left for voicemail');
  });
  return rows;
}

/** One line for the call's overview: where it went, step by step. */
export function pathSummary(rows: PathRow[]): string {
  return rows.map((r) => (r.kind === 'ivr' && r.said.length ? `${r.title}, ${r.said[0]}` : r.title)).join(' → ');
}

const ICON = (k: PathRow['kind']) => (k === 'voicemail' ? 'mail' : k === 'outside' ? 'callOut' : KIND_ICON[k]);

export function CallPath({ tenantId, steps, endedAt, answeredBy, tz }: {
  tenantId: string; steps: CallPathStep[]; endedAt: string; tz: string;
  answeredBy: { number: string | null; name: string | null; at: string } | null;
}) {
  const { live, ready } = useLiveRouting(tenantId);
  if (!ready) return <div className="arc-skeleton" style={{ height: 160 }} />;
  const rows = pathRows(steps, live, Math.round(new Date(endedAt).getTime() / 1000), answeredBy);
  return (
    <div className="arc-card arc-card-body">
      <ol className="arc-path" aria-label="How the call went">
        {rows.map((r, i) => (
          <li key={i} className="arc-path-step">
            <span className="arc-path-time arc-num">{formatTime(new Date(r.at * 1000).toISOString(), tz)}</span>
            <span className="arc-flow-node-icon" data-group={r.kind === 'voicemail' || r.kind === 'outside' ? 'people' : KIND_GROUP[r.kind]}>
              <Icon name={ICON(r.kind)} size={13} />
            </span>
            <span className="arc-path-body">
              <span className="arc-path-head"><strong>{r.title}</strong><span className="arc-path-what">{r.what}</span></span>
              {r.said.length > 0 && <span className="arc-path-said">{r.said.join(', ')}</span>}
            </span>
            {r.seconds !== null && <span className="arc-path-for arc-num">{formatDuration(r.seconds)}</span>}
          </li>
        ))}
        <li className="arc-path-step arc-path-end">
          <span className="arc-path-time arc-num">{formatTime(endedAt, tz)}</span>
          <span className="arc-flow-node-icon"><Icon name="close" size={13} /></span>
          <span className="arc-path-body"><span className="arc-path-head"><strong>Call ended</strong></span></span>
        </li>
      </ol>
      <p className="arc-hint" style={{ margin: '10px 0 0' }}>
        Steps are named as they are now; one deleted since says so.
      </p>
    </div>
  );
}

export { useLiveRouting };
